import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { post } from '../../utils/request';
import { fetchAndCacheCurrentUser } from '../../utils/currentUser';
import { useToast } from '../Toast/Toast';
import { t } from '../../utils/i18n';
import { VK_CONFIG } from '../../utils/vkConfig';
import { gameHomePath } from '../../utils/routes';
import {
  clearVkPkceSession,
  readVkPkceState,
  readVkPkceVerifier,
} from '../../utils/vkPkce';

/**
 * VK ID OAuth 回调页面
 * 处理授权后带 code 的重定向，调用后端换取 token
 */
const VKCallback = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [status, setStatus] = useState('loading'); // loading | success | error

    useEffect(() => {
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const deviceId = searchParams.get('device_id');
        const state = searchParams.get('state');

        const basePath = process.env.PUBLIC_URL || "/mg";
        const routerHomePath = gameHomePath();
        const fullHomeHref = `${basePath}${routerHomePath}`;

        if (error) {
            clearVkPkceSession();
            toast.error(
              searchParams.get('error_description') ||
                t('vk.error.auth', 'VK authorization failed')
            );
            setStatus('error');
            setTimeout(() => navigate(routerHomePath), 1500);
            return;
        }

        if (!code) {
            clearVkPkceSession();
            toast.error(t('vk.error.noCode', 'Authorization code not received'));
            setStatus('error');
            setTimeout(() => navigate(routerHomePath), 1500);
            return;
        }

        const expectedState = readVkPkceState();
        const codeVerifier = readVkPkceVerifier();
        if (!codeVerifier || !expectedState || state !== expectedState) {
            clearVkPkceSession();
            toast.error(t('vk.error.stateMismatch', 'VK login state verification failed'));
            setStatus('error');
            setTimeout(() => navigate(routerHomePath), 1500);
            return;
        }

        const doLogin = async () => {
            try {
                const redirectUri = VK_CONFIG.getRedirectUri();
                const data = await post('/user/vk-login', {
                    code,
                    platform: 'web',
                    redirectUri,
                    redirect_uri: redirectUri,
                    codeVerifier,
                    code_verifier: codeVerifier,
                    state,
                    deviceId,
                    device_id: deviceId,
                });

                if (data.data?.token) {
                    localStorage.setItem('token', data.data.token);
                    await fetchAndCacheCurrentUser().catch(() => {});
                    toast.success(t('vk.success.login', 'VK login successful'));
                    setStatus('success');
                    setTimeout(() => { window.location.href = fullHomeHref; }, 800);
                } else {
                    throw new Error(t('vk.error.invalidResponse', 'Invalid login response'));
                }
            } catch (err) {
                console.error('VK 登录错误:', err);
                toast.error(err.message || t('vk.error.login', 'VK login failed'));
                setStatus('error');
                setTimeout(() => navigate(routerHomePath), 2000);
            } finally {
                clearVkPkceSession();
            }
        };

        doLogin();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: '#1A1A1A',
            color: '#fff',
            fontFamily: 'GT Walsheim Pro, sans-serif'
        }}>
            {status === 'loading' && <p>{t('vk.status.loading', 'Completing VK login...')}</p>}
            {status === 'success' && <p>{t('vk.status.success', 'Login successful, redirecting...')}</p>}
            {status === 'error' && <p>{t('vk.status.error', 'Login failed, returning to homepage...')}</p>}
        </div>
    );
};

export default VKCallback;
